import { PUBLIC_SITE_THEME_STORAGE_KEY, THEME_STORAGE_KEY, type ResolvedTheme } from '@/contexts/theme/shared';

/**
 * Home 承载的四类区域。区域由路径判定，不由应用名或登录态推导。
 * - public：公开内容，URL 带语言前缀，可索引
 * - shared-site：访客访问的 Agent 站点，不套产品准入
 * - product：登录后的工作台
 * - protocol：OAuth 回调、静默登录、组件预览等协议页
 */
export const APP_AREAS = ['public', 'product', 'shared-site', 'protocol'] as const;
export type AppArea = (typeof APP_AREAS)[number];

export interface AppPolicy {
    area: AppArea;
    /** 强制展示主题；未设置时沿用用户偏好。 */
    forcedTheme?: ResolvedTheme;
    /** 首帧脚本与服务端渲染共用的初始主题。 */
    initialTheme: ResolvedTheme;
    /**
     * 页面统计口径：`url` 按完整地址计页（公开区现有口径）；
     * `route-template` 按路由模板计页，同页 query 变化不重复计；`none` 不计页。
     */
    pageViews: 'url' | 'route-template' | 'none';
    /**
     * 接口请求的归因头：`registration` 在本页生命周期的首个请求前等一次 GA 标识
     * （登录回跳后的建号请求要带上），之后直接复用；`immediate` 同步读取已有值，不等待。
     */
    requestAttribution: 'registration' | 'immediate';
    /** 主题偏好的存储位置；访客站点与主站账号互不影响。 */
    themeStorageKey: string;
    /**
     * 未显式指定时登录完成后的去向。公开页登录进工作台；其余区域回到当前 URL，
     * 不把用户从正在处理的页面带走。无尾斜杠，避免多一次 308。
     */
    loginReturnTo?: string;
}

const AREA_POLICIES: Record<AppArea, AppPolicy> = {
    public: { area: 'public', forcedTheme: 'light', initialTheme: 'light', pageViews: 'url', requestAttribution: 'immediate', themeStorageKey: THEME_STORAGE_KEY, loginReturnTo: '/workspace' },
    'shared-site': { area: 'shared-site', forcedTheme: 'light', initialTheme: 'light', pageViews: 'route-template', requestAttribution: 'immediate', themeStorageKey: PUBLIC_SITE_THEME_STORAGE_KEY },
    product: { area: 'product', initialTheme: 'light', pageViews: 'route-template', requestAttribution: 'registration', themeStorageKey: THEME_STORAGE_KEY },
    // 协议页只是跳转中转站，计页会污染漏斗。
    protocol: { area: 'protocol', forcedTheme: 'light', initialTheme: 'light', pageViews: 'none', requestAttribution: 'immediate', themeStorageKey: THEME_STORAGE_KEY },
};

export function getAppPolicy(area: AppArea): AppPolicy {
    return AREA_POLICIES[area];
}
